import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Perfect Signs Canada",
    "url": "https://perfectsignscanada.com/about.html",
    "description": "Perfect Signs Canada helps businesses grow with custom signage, wraps, websites, SEO, marketing, and POS support.",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Perfect Signs Canada",
      "url": "https://perfectsignscanada.com/"
    },
    "about": {
      "@type": "LocalBusiness",
      "name": "Perfect Signs Canada",
      "telephone": "+1-780-554-3949",
      "email": "info@perfectsignscanada.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "6109 176 Ave NW",
        "addressLocality": "Edmonton",
        "addressRegion": "AB",
        "postalCode": "T5Y 0K8",
        "addressCountry": "CA"
      }
    }
  };

  return (
    <>
      <SEO 
        title="About Perfect Signs Canada | Signage, Websites & Marketing Experts"
        description="Learn about Perfect Signs Canada, an Edmonton-based team helping businesses grow with custom signage, wraps, websites, SEO, marketing, and POS support across Canada."
        keywords="about Perfect Signs Canada, custom signage Edmonton, business signs Canada, vehicle wraps, website design, SEO, digital marketing, POS systems Edmonton"
        canonical="https://perfectsignscanada.com/about.html"
        schema={aboutSchema}
      />

      {/* ======= Breadcrumbs ======= */}
      <div className="breadcrumbs d-flex align-items-center" style={{ backgroundImage: "url('/assets/img/about-header.jpg')" }}>
        <div className="container position-relative d-flex flex-column align-items-center" data-aos="fade-up">
          <p className="breadcrumbs-eyebrow">About Perfect Signs Canada</p>
          <h2 className="text-center">Practical signage and digital services for businesses that want to be seen.</h2>
          <p className="breadcrumbs-summary">We combine custom signs, wraps, websites, marketing, and technology support so your brand looks consistent in the street, online, and at the point of sale.</p>
          <ol>
            <li><Link to="/">Home</Link></li>
            <li>About</li>
          </ol>
        </div>
      </div>
      {/* End Breadcrumbs */}

      {/* ======= About Intro Section ======= */}
      <section id="about" className="about about-intro">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4 align-items-center">
            <div className="col-lg-5" data-aos="fade-up" data-aos-delay="100">
              <img src="/assets/img/about.jpg" className="img-fluid about-photo" alt="Perfect Signs Canada signage and design work" />
            </div>
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="250">
              <div className="content ps-lg-4">
                <p className="section-kicker">Who we are</p>
                <h3>We help businesses look professional everywhere customers meet them.</h3>
                <p>
                  Perfect Signs Canada is an Edmonton-based signage and digital services team serving businesses across Canada. We started with a simple belief: a business should be easy to notice, easy to understand, and easy to trust.
                </p>
                <p>
                  That means strong storefront signs, clear vehicle wraps, polished window graphics, reliable websites, better search visibility, and practical technology support working together instead of feeling like separate projects.
                </p>
                <ul>
                  <li><i className="bi bi-check-circle-fill text-primary"></i> Custom signage, banners, wraps, pylon signs, and channel letters.</li>
                  <li><i className="bi bi-check-circle-fill text-primary"></i> Websites, SEO, digital marketing, hosting, and business email support.</li>
                  <li><i className="bi bi-check-circle-fill text-primary"></i> POS and technology solutions shaped around real business workflows.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End About Intro Section */}

      {/* ======= About Values Section ======= */}
      <section className="about-values">
        <div className="container" data-aos="fade-up">
          <div className="section-header" data-aos="fade-up">
            <h2>How We Work</h2>
            <p>Simple process, clear communication, and work that respects your budget and timeline.</p>
          </div>
          <div className="row gy-4">
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="100">
              <div className="value-card">
                <i className="bi bi-chat-square-text"></i>
                <h3>Listen First</h3>
                <p>We learn your business, location, audience, and goals before recommending materials or platforms.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="200">
              <div className="value-card">
                <i className="bi bi-pencil-square"></i>
                <h3>Design Clearly</h3>
                <p>We focus on readable, brand-aware design that works in real conditions, not just on a screen.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="300">
              <div className="value-card">
                <i className="bi bi-tools"></i>
                <h3>Build Carefully</h3>
                <p>We care about materials, installation details, responsive websites, and reliable technical setup.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="400">
              <div className="value-card">
                <i className="bi bi-people"></i>
                <h3>Support Honestly</h3>
                <p>We keep communication straightforward and help you choose what is useful for your business.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End About Values Section */}

      {/* ======= About Offer Section ======= */}
      <section className="about-offer">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4 align-items-stretch">
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
              <div className="offer-panel signage-panel">
                <span>Signage</span>
                <h2>Built to capture attention in the real world.</h2>
                <p>We create storefront signs, portable road signs, banners, window graphics, wall wraps, vehicle wraps, pylon signs, and channel letters that help customers understand who you are quickly.</p>
                <Link to="/signage">Explore signage work <i className="bi bi-arrow-right"></i></Link>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
              <div className="offer-panel digital-panel">
                <span>Digital Services</span>
                <h2>Built to help customers find and choose you online.</h2>
                <p>We support businesses with websites, SEO, digital marketing, hosting, business email, APIs, automation, and POS solutions that make daily operations easier.</p>
                <Link to="/services">Explore IT services <i className="bi bi-arrow-right"></i></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End About Offer Section */}

      {/* ======= About CTA Section ======= */}
      <section className="about-cta">
        <div className="container" data-aos="fade-up">
          <div className="row align-items-center gy-4">
            <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
              <p className="section-kicker">Ready when you are</p>
              <h2>Let’s make your business easier to notice, understand, and remember.</h2>
              <p>Whether you need one sign, a full storefront refresh, a new website, or digital support around your brand, we can help you choose a practical next step.</p>
            </div>
            <div className="col-lg-4 text-lg-end" data-aos="fade-up" data-aos-delay="250">
              <Link to="/contact" className="about-cta-btn">Request a Quote</Link>
            </div>
          </div>
        </div>
      </section>
      {/* End About CTA Section */}
    </>
  );
}
